import type { ChatMessage, MessageDeliveryStatus } from '@teo/widget-shared';
import { sortMessagesByDate } from '@teo/widget-shared';
import { areSameUserSend } from './message-store.js';

const STATUS_RANK: Record<MessageDeliveryStatus, number> = {
  sending: 0,
  failed: 0,
  sent: 1,
  delivered: 2,
  read: 3,
};

/** Higher delivery progress wins; `sending` is lowest among non-failed states. */
export function maxDeliveryStatus(
  a?: MessageDeliveryStatus,
  b?: MessageDeliveryStatus,
): MessageDeliveryStatus | undefined {
  if (!a) return b;
  if (!b) return a;
  if (a === 'failed' || b === 'failed') return a === 'failed' ? a : b;
  return STATUS_RANK[a] >= STATUS_RANK[b] ? a : b;
}

function findLocalUserMatch(
  serverMessage: ChatMessage,
  localMessages: ChatMessage[],
): ChatMessage | undefined {
  if (serverMessage.role !== 'user') return undefined;

  if (serverMessage.clientId) {
    const byClientId = localMessages.find(
      (local) =>
        local.clientId === serverMessage.clientId ||
        local.id === serverMessage.clientId,
    );
    if (byClientId) return byClientId;
  }

  return localMessages.find((local) => areSameUserSend(local, serverMessage));
}

/** Keep higher local delivery status after server merge (sent from API). */
export function mergeOutgoingDeliveryStatus(
  messages: ChatMessage[],
  localMessages: ChatMessage[],
): ChatMessage[] {
  return messages.map((message) => {
    if (message.role !== 'user') return message;

    const local = findLocalUserMatch(message, localMessages);
    if (!local?.status) return message;

    const status = maxDeliveryStatus(message.status, local.status);
    if (!status || status === message.status) return message;
    return { ...message, status };
  });
}

/** Bot/manager replied → user messages before that time are read. */
export function applyOutgoingReadReceipts(messages: ChatMessage[]): ChatMessage[] {
  const sorted = sortMessagesByDate(messages);
  let latestIncomingAt = 0;

  for (const message of sorted) {
    if (message.role === 'bot' || message.role === 'manager') {
      latestIncomingAt = Math.max(
        latestIncomingAt,
        new Date(message.createdAt).getTime(),
      );
    }
  }

  if (latestIncomingAt === 0) {
    return messages;
  }

  return messages.map((message) => {
    if (message.role !== 'user') return message;
    if (message.status === 'failed' || message.status === 'sending') {
      return message;
    }

    const sentAt = new Date(message.createdAt).getTime();
    if (sentAt <= latestIncomingAt) {
      if (message.status === 'read') return message;
      return { ...message, status: 'read' as const };
    }

    return message;
  });
}

function latestSentUserMessage(messages: ChatMessage[]): ChatMessage | undefined {
  let latest: ChatMessage | undefined;
  let latestAt = 0;

  for (const message of messages) {
    if (message.role !== 'user' || message.status !== 'sent') continue;
    const createdAt = new Date(message.createdAt).getTime();
    if (!latest || createdAt >= latestAt) {
      latest = message;
      latestAt = createdAt;
    }
  }

  return latest;
}

/** Typing indicator → latest outbound `sent` becomes `delivered` (no WS from backend). */
export function markUserMessagesDelivered(messages: ChatMessage[]): ChatMessage[] {
  const latest = latestSentUserMessage(messages);
  if (!latest) return messages;

  const latestKey = latest.clientId ?? latest.id;
  let changed = false;
  const next = messages.map((message) => {
    if (message.role !== 'user' || message.status !== 'sent') {
      return message;
    }
    const messageKey = message.clientId ?? message.id;
    if (messageKey !== latestKey) return message;
    changed = true;
    return { ...message, status: 'delivered' as const };
  });
  // Preserve array identity when nothing changed so the typing tick doesn't
  // trigger a redundant MessageList render.
  return changed ? next : messages;
}

export function applyOutgoingStatusPipeline(
  messages: ChatMessage[],
  localMessages: ChatMessage[],
): ChatMessage[] {
  const withLocalStatus = mergeOutgoingDeliveryStatus(messages, localMessages);
  return applyOutgoingReadReceipts(withLocalStatus);
}
